// src/pages/ChatBot/query-logs/lexical.ts
//
// Frontend mirror of the backend query-side lexical primitives
// (askSmart-backend src/utils/chatbot/utils/lexical.ts) plus the term-match
// rule the Inspect trace uses to show query↔chunk keyword overlap. The
// full-text axis is rank-only (Cosmos never returns FullTextScore), so
// matched terms are the visible signal. Keep STOPWORDS and tokenize()
// byte-identical to the backend.

// Mirror of the backend retrieval config maxFullTextTerms (not tenant-configurable) —
// caps the client-side fallback tokenization for docs without persisted searchTerms.
export const MAX_FULL_TEXT_TERMS = 10;

const STOPWORDS = new Set([
  'a','an','and','are','as','at','be','by','do','for','from','has','have',
  'he','her','him','his','how','i','if','in','is','it','its','me','my',
  'no','not','of','on','or','our','out','she','so','that','the','their',
  'them','they','this','to','up','us','was','we','were','what','when',
  'where','which','who','will','with','you','your',
  // — expanded standard function words —
  'about','above','after','again','against','all','also','am','among','any',
  'because','been','before','being','below','between','both','but','cannot','could',
  'did','does','doing','done','down','during','each','either','few','further',
  'had','having','here','hers','herself','himself','into','itself','just','more',
  'most','much','myself','near','neither','nor','now','off','once','only',
  'onto','other','others','ours','ourselves','over','own','same','since','some',
  'such','than','then','there','these','those','through','too','under','until',
  'upon','very','whatever','whom','whose','why','would','yet',
  // — conversational question filler — mirrors the backend list exactly —
  'can','find','get','give','know','like','list','many','need','please','show','tell','want',
  // — contraction remnants (the backend sanitizer strips apostrophes without a space,
  // so "what's" tokenizes as the junk token "whats") — mirrors the backend list exactly —
  'aint','arent','cant','couldnt','didnt','doesnt','dont','hadnt','hasnt','havent',
  'heres','hes','hows','im','isnt','ive','lets','shes','shouldnt','thats','theres',
  'theyd','theyll','theyre','theyve','wasnt','werent','weve','whats','wheres','whos',
  'wont','wouldnt','youd','youll','youre','youve',
]);

// Lowercase alphanumeric terms minus stopwords, deduped in first-seen order —
// exact mirror of the backend tokenizer.
export function tokenize(text: string): string[] {
  return [
    ...new Set(
      text
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length > 1 && !STOPWORDS.has(t)),
    ),
  ];
}

// Cosmos FullTextScore stems server-side, so exact-token equality under-reports
// ("contain" vs "containing"). Deterministic approximation: exact match, a
// SYMMETRIC prefix match with a ≥4-char floor on whichever side is the prefix
// (query "contains" matches chunk "contain" and vice versa), plus a y→ies fold —
// the one common English inflection a prefix check cannot reach ('policies' does
// NOT start with 'policy'). The same rule drives chips, coverage, and the
// expanded-text highlighter so they can never disagree.
// MIRROR CONTRACT: must stay byte-compatible with termMatchesToken in the
// backend's utils/chatbot/utils/lexical.ts — the retrieval text-axis floor
// (minTextMatchedTerms) filters by the same rule this trace displays.
const PREFIX_MATCH_MIN_TERM_LENGTH = 4;

function foldPluralY(word: string): string {
  return word.length > 3 && word.endsWith('ies') ? `${word.slice(0, -3)}y` : word;
}

function matchesDirect(a: string, b: string): boolean {
  return (
    a === b ||
    (a.length >= PREFIX_MATCH_MIN_TERM_LENGTH && b.startsWith(a)) ||
    (b.length >= PREFIX_MATCH_MIN_TERM_LENGTH && a.startsWith(b))
  );
}

export function termMatchesToken(term: string, tokenLower: string): boolean {
  return matchesDirect(term, tokenLower) || matchesDirect(foldPluralY(term), foldPluralY(tokenLower));
}

// The subset of `terms` with at least one matching token in the chunk text, in
// `terms` order. The chunk side keeps its raw surface tokens (no stopword or
// length filter) — matching is against what the chunk actually says.
export function getMatchedTerms(chunkText: string, terms: string[]): string[] {
  const tokens = [...new Set(chunkText.toLowerCase().split(/[^a-z0-9]+/))];
  return terms.filter((term) => tokens.some((token) => termMatchesToken(term, token)));
}
